import { useState, useCallback } from "react";
import { Box, Stack } from "@mui/material";
import styled from "@emotion/styled";
import FilePreview from "./components/FilePreview";
import Uploaders from "./components/Uploaders";
import { FileType } from "src/api/types";
import { resizeImage } from "src/utils/resizeImage";

const convertToImageType = async (
  file: File | Blob,
  imageType: string = "image/jpeg",
) => {
  if (["image/heic", "image/heif"].includes(file.type)) {
    const { default: heic2any } = await import("heic2any");

    const result = await heic2any({
      blob: file,
      toType: imageType,
    });

    if (Array.isArray(result)) {
      return result[0];
    }

    return result;
  }

  return file;
};

export const ImageCol = styled.div`
  position: relative;
  width: 100%;
`;

interface UploaderProps {
  title: string | null;
  subtitle: string | null;
  name: string;
  file: FileType | null;
  folder: string;
  showConfirmationText?: boolean;
  setFilePath: (file: FileType | null) => void;
  subImageTextOne?: string | null;
  subImageTextTwo?: string | null;
  subImageTextMobile?: string | null;
}

const ImageUploader = ({
  title,
  subtitle,
  file,
  setFilePath,
  name,

  subImageTextOne = null,
  subImageTextTwo = null,
  subImageTextMobile = null,
}: UploaderProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const deleteFile = useCallback(() => {
    setFilePath(null);
  }, [setFilePath]);

  const handleFile = useCallback(
    async (file: File | Blob) => {
      const blob = await convertToImageType(file);

      resizeImage(blob, 1024).then((base64) => {
        setFilePath({
          fileToUpload: base64,
          type: file.type,
          name,
        });
      });
    },
    [name, setFilePath],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(async (currentFile) => {
        if (
          [
            "image/png",
            "image/jpeg",
            "image/webp",
            "image/heic",
            "image/heif",
          ].includes(currentFile.type)
        ) {
          setIsLoading(true);
          try {
            await handleFile(currentFile);
          } finally {
            setIsLoading(false);
          }
        }
      });
    },
    [handleFile],
  );

  return (
    <>
      <Stack gap="5px" alignItems="center">
        <ImageCol>
          <Box>
            {file ? (
              <FilePreview
                onRemove={deleteFile}
                file={file.fileToUpload}
                title={title || ""}
              />
            ) : (
              <Stack gap={4}>
                <Uploaders
                  title={title}
                  subtitle={subtitle}
                  onDrop={onDrop}
                  subImageTextOne={subImageTextOne}
                  subImageTextTwo={subImageTextTwo}
                  subImageTextMobile={subImageTextMobile}
                  isLoading={isLoading}
                />
              </Stack>
            )}
          </Box>
        </ImageCol>
      </Stack>
    </>
  );
};

export default ImageUploader;
